import EmptyPlaceholder from "@/components/ui/EmptyPlaceholder";
import ErrorDisplay from "@/components/ui/ErrorDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import { UseQueryResult } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useState } from "react";
import PaginationFooterControls from "./PaginationFooterControls";

export interface PaginatedGroupProps<T, TData extends Record<string, unknown>> {
  query: (
    page: number,
    pageSize: number,
  ) => UseQueryResult<TData, unknown> | null;
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => string | number;
  title?: string;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  emptyMessage?: string;
  showPageSizeSelector?: boolean;
  className?: string;
  gridClassName?: string;
  extractData?: (data: TData) => {
    data: T[];
    totalPages: number;
    totalItems: number;
  };
  onItemClick?: (item: T) => void;
  prefix?: ReactNode;
  suffix?: ReactNode;
  viewType?: "grid" | "list";
}

const PaginatedGroup = <T, TData extends Record<string, unknown>>({
  query,
  renderItem,
  getItemKey,
  title = "Untitled Group",
  initialPageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  emptyMessage = "No items found",
  showPageSizeSelector = true,
  className = "",
  gridClassName = "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-fr",
  extractData = (data: TData) =>
    data as unknown as { data: T[]; totalPages: number; totalItems: number },
  onItemClick,
  prefix,
  suffix,
  viewType = "list",
}: PaginatedGroupProps<T, TData>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const queryResult = query(currentPage, pageSize);

  const {
    data: queryData,
    isLoading,
    isError,
  } = queryResult || { data: undefined, isLoading: false, isError: false };

  const {
    data: items = [],
    totalPages = 0,
    totalItems = 0,
  } = queryData
    ? extractData(queryData)
    : { data: [] as T[], totalPages: 0, totalItems: 0 };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const containerClassName =
    viewType === "list" ? "flex flex-col space-y-4" : gridClassName;

  return (
    <div className={`${className} box-border w-full overflow-visible`}>
      {title && (
        <h2 className="mb-4 flex items-center font-medium">
          {title}
          {totalItems > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({totalItems})
            </span>
          )}
        </h2>
      )}

      <div className={containerClassName}>
        {!isError && items.length ? prefix : null}

        {isLoading ? (
          Array.from({
            length: pageSize > 8 ? 8 : pageSize,
          }).map((_, index) => (
            <Skeleton key={index} height={72} className="w-full rounded-sm" />
          ))
        ) : isError ? (
          <ErrorDisplay
            className={viewType === "grid" ? "col-span-4 w-full" : "w-full"}
          >
            Something went wrong, unable to get items
          </ErrorDisplay>
        ) : items.length === 0 ? (
          <EmptyPlaceholder
            message={emptyMessage}
            icon
            className={viewType === "grid" ? "col-span-4" : ""}
          />
        ) : (
          items.map((item: T) => (
            <div
              key={getItemKey(item)}
              onClick={() => onItemClick && onItemClick(item)}
              className={viewType === "list" ? "w-full" : ""}
            >
              {renderItem(item)}
            </div>
          ))
        )}

        {!isError && items.length ? suffix : null}
      </div>

      {items.length < totalItems && (
        <PaginationFooterControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={pageSize}
          totalItems={totalItems}
          showPageSizeSelector={showPageSizeSelector}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={handlePageSizeChange}
          className="mt-6"
        />
      )}
    </div>
  );
};

export default observer(PaginatedGroup);
